// Only define QuizAnalytics if it hasn't been defined yet
if (typeof QuizAnalytics === 'undefined') {
  class QuizAnalytics {
    constructor(quizElement) {
      this.quizElement = quizElement;
      this.quizId = quizElement.id;
      this.totalSteps = parseInt(quizElement.dataset.totalSteps) || 0;
      this.sessionId = this.generateSessionId();
      this.quizStarted = false;
      this.startTime = null;
      this.currentStep = 1;
      this.stepTimings = {};
      this.viewedStepKeys = new Set();
      
      // Get test configuration from quiz element
      this.testConfig = {
        active: quizElement.dataset.testActive === 'true',
        uniqueTestId: quizElement.dataset.uniqueTestId || 'no_test',
        userVariant: quizElement.dataset.userVariant || 'control',
        testPercentage: parseInt(quizElement.dataset.testPercentage) || 0,
        testStartDate: quizElement.dataset.testStartDate || ''
      };
      
      this.initializeGA4();
      this.bindEvents();
    }
  
  initializeGA4() {
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function() {
      dataLayer.push(arguments);
    };

    // Load gtag.js if not already on the page (site-analytics.js normally
    // handles this, but guard here in case of load-order differences)
    if (!document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) {
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://www.googletagmanager.com/gtag/js?id=G-Z5BWJ1WW16';
      document.head.appendChild(script);
    }

    if (!window._acGA4Configured) {
      window._acGA4Configured = true;
      gtag('js', new Date());
      gtag('config', 'G-Z5BWJ1WW16');
    }
  }
  
  generateSessionId() {
    return `quiz_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
  
  bindEvents() {
    // Track quiz completion
    this.quizElement.addEventListener('quiz:complete', (e) => {
      this.trackQuizComplete(e.detail);
    });
    
    // Track cart add success
    this.quizElement.addEventListener('cart-add-success', (e) => {
      this.trackCartAdd(e.detail);
    });
    
    // Track cart add error
    this.quizElement.addEventListener('cart-add-error', (e) => {
      this.trackError('cart_add_error', e.detail);
    });
    
    // Track state changes
    this.quizElement.addEventListener('state-changed', (e) => {
      this.trackStateChange(e.detail);
    });
    
    // Track page unload for abandonment
    window.addEventListener('beforeunload', () => {
      if (!this.quizStarted) return;
      const currentStep = parseInt(this.quizElement.state?.currentStep) || this.currentStep;
      if (currentStep < this.totalSteps) {
        this.trackQuizAbandon(currentStep);
      }
    });
  }
  
  trackQuizStart() {
    this.quizStarted = true;
    this.startTime = Date.now();
    const eventData = {
      event: 'quiz_start',
      quiz_id: this.quizId,
      quiz_session_id: this.sessionId,
      ac_session_id: window.SiteAnalytics?.sessionId || null,
      total_steps: this.totalSteps,
      quiz_test_active: this.testConfig.active,
      quiz_test_id: this.testConfig.uniqueTestId,
      quiz_user_variant: this.testConfig.userVariant,
      test_percentage: this.testConfig.testPercentage,
      test_start_date: this.testConfig.testStartDate,
      timestamp: new Date().toISOString()
    };
    
    gtag('event', 'quiz_start', eventData);
  }
  
  trackStepView(stepNumber, stepType, stepId, variantInfo = {}) {
    if (!this.quizStarted) return;

    // Track time spent on previous step
    if (this.currentStep !== stepNumber && this.stepTimings[this.currentStep]) {
      const timeOnStep = Date.now() - this.stepTimings[this.currentStep];
      this.trackTimeOnStep(this.currentStep, timeOnStep);
    }
    
    this.currentStep = stepNumber;
    this.stepTimings[stepNumber] = Date.now();

    // Deduplicate step views within the same quiz session.
    // Prefer a stable step id (block id) when available; fall back to step number.
    const stepKey = `${this.quizId}::${this.sessionId}::${stepId || stepNumber}`;
    const isRepeatView = this.viewedStepKeys.has(stepKey);
    if (!isRepeatView) {
      this.viewedStepKeys.add(stepKey);
    }
    
    const eventData = {
      quiz_id: this.quizId,
      quiz_session_id: this.sessionId,
      ac_session_id: window.SiteAnalytics?.sessionId || null,
      step_number: stepNumber,
      step_type: stepType,
      step_id: stepId,
      time_on_quiz: Math.round((Date.now() - this.startTime) / 1000),
      
      // Global test info
      quiz_test_active: this.testConfig.active,
      quiz_test_id: this.testConfig.uniqueTestId,
      quiz_user_variant: this.testConfig.userVariant,
      
      // Step-specific test info
      step_test_active: variantInfo.testActive || false,
      step_variant: variantInfo.variant || 'control',
      step_variant_group: variantInfo.variantGroup || 'none'
    };
    
    if (!isRepeatView) {
      gtag('event', 'quiz_step_view', eventData);
      
      // Special handling for cross-sell steps (also deduped by step)
      if (stepType === 'cross-sell') {
        this.trackCrossSellView(stepId, variantInfo);
      }
    }
  }
  
  trackTimeOnStep(stepNumber, timeSpent) {
    const eventData = {
      quiz_id: this.quizId,
      quiz_session_id: this.sessionId,
      ac_session_id: window.SiteAnalytics?.sessionId || null,
      step_number: stepNumber,
      time_spent_ms: timeSpent,
      time_spent_seconds: Math.round(timeSpent / 1000)
    };
    
    gtag('event', 'quiz_step_time', eventData);
  }
  
  trackCrossSellView(stepId, variantInfo) {
    const step = this.quizElement.querySelector(`.quiz-step[data-block-id="${stepId}"]`);
    const crossSellProduct = step?.querySelector('.quiz-cross-sell__upsell');
    
    if (!crossSellProduct) {
      return;
    }
    
    const eventData = {
      quiz_id: this.quizId,
      quiz_session_id: this.sessionId,
      ac_session_id: window.SiteAnalytics?.sessionId || null,
      step_id: stepId,
      test_active: variantInfo.testActive,
      variant_group: variantInfo.variantGroup,
      variant: variantInfo.variant,
      product_id: crossSellProduct.dataset.productId,
      product_variant: crossSellProduct.dataset.variantTitle,
      product_handle: crossSellProduct.dataset.productHandle,
      product_title: crossSellProduct.dataset.productTitle,
      product_price: parseFloat(crossSellProduct.dataset.subscriptionPrice) || parseFloat(crossSellProduct.dataset.onetimePrice),
      currency: window.Shopify?.currency?.active || 'USD',
      time_on_quiz: Math.round((Date.now() - this.startTime) / 1000)
    };
    
    gtag('event', 'quiz_cross_sell_view', eventData);
    
    // Also send standard GA4 ecommerce event
    const ecommerceData = {
      currency: eventData.currency,
      value: eventData.product_price,
      quiz_id: this.quizId,
      quiz_session_id: this.sessionId,
      ac_session_id: window.SiteAnalytics?.sessionId || null,
      items: [{
        item_id: eventData.product_id,
        item_name: eventData.product_title,
        price: eventData.product_price,
        item_category: 'cross-sell',
        item_variant: variantInfo.testActive ? variantInfo.variant : 'no_test'
      }]
    };
    
    gtag('event', 'view_item', ecommerceData);
  }
  
  trackCrossSellClick(crossSellProduct) {
    const step = crossSellProduct.closest('.quiz-step');

    if (!step || !crossSellProduct) {
      return;
    }
    
    const variantGroup = step.dataset.variantGroup;
    const variant = step.dataset.variantType;
    const stepId = step.dataset.blockId;
    const selectedOption = step.querySelector('input[data-cross-sell-option]:checked')?.dataset.crossSellOption;
    const productPrice = selectedOption === 'onetime'
      ? parseFloat(crossSellProduct.dataset.onetimePrice)
      : parseFloat(crossSellProduct.dataset.subscriptionPrice);

    const eventData = {
      quiz_id: this.quizId,
      quiz_session_id: this.sessionId,
      ac_session_id: window.SiteAnalytics?.sessionId || null,
      step_id: stepId,
      variant_group: variantGroup || 'none',
      variant: variant || 'control',
      test_active: !!variantGroup,
      product_id: crossSellProduct.dataset.productId,
      product_variant: crossSellProduct.dataset.variantTitle,
      product_handle: crossSellProduct.dataset.productHandle,
      product_title: crossSellProduct.dataset.productTitle,
      product_price: productPrice,
      currency: window.Shopify?.currency?.active || 'USD',
      click_time: Math.round((Date.now() - this.startTime) / 1000)
    };
    
    gtag('event', 'quiz_cross_sell_click', eventData);
  }
  
  trackInteraction(interactionType, details) {
    if (!this.quizStarted) return;

    const eventData = {
      quiz_id: this.quizId,
      quiz_session_id: this.sessionId,
      ac_session_id: window.SiteAnalytics?.sessionId || null,
      interaction_type: interactionType,
      current_step: this.currentStep,
      time_on_quiz: Math.round((Date.now() - this.startTime) / 1000),
      test_active: this.testConfig.active,
      user_variant: this.testConfig.userVariant,
      ...details
    };
    
    gtag('event', 'quiz_interaction', eventData);
  }
  
  trackStateChange(details) {
    // Only track significant state changes
    if (!this.quizStarted) return;

    const significantKeys = [
      'selectedMainVariantId', 
      'subscriptionType', 
      'crossSellProducts',
      'currentStep'
    ];
    
    const isSignificant = significantKeys.includes(details.key);
    
    if (isSignificant) {
      this.trackInteraction('state_change', {
        state_key: details.key,
        state_value: details.value
      });
    }
  }
  
  trackQuizComplete(completionData = {}) {
    // Track time on final step
    if (this.stepTimings[this.currentStep]) {
      const timeOnStep = Date.now() - this.stepTimings[this.currentStep];
      this.trackTimeOnStep(this.currentStep, timeOnStep);
    }
    
    const eventData = {
      quiz_id: this.quizId,
      quiz_session_id: this.sessionId,
      ac_session_id: window.SiteAnalytics?.sessionId || null,
      total_time: Math.round((Date.now() - this.startTime) / 1000),
      steps_completed: this.totalSteps,
      test_active: this.testConfig.active,
      test_id: this.testConfig.uniqueTestId,
      user_variant: this.testConfig.userVariant,
      ...completionData
    };
    
    gtag('event', 'quiz_complete', eventData);
    
    // Track conversion if test is active
    if (this.testConfig.active) {
      const conversionData = {
        test_id: this.testConfig.uniqueTestId,
        variant: this.testConfig.userVariant,
        quiz_id: this.quizId,
        session_id: this.sessionId
      };
      
      gtag('event', 'ab_test_conversion', conversionData);
    }
  }
  
  trackQuizAbandon(currentStep) {
    const eventData = {
      quiz_id: this.quizId,
      quiz_session_id: this.sessionId,
      ac_session_id: window.SiteAnalytics?.sessionId || null,
      abandoned_at_step: currentStep,
      time_on_quiz: Math.round((Date.now() - this.startTime) / 1000),
      test_active: this.testConfig.active,
      test_id: this.testConfig.uniqueTestId,
      user_variant: this.testConfig.userVariant,
      completion_percentage: Math.round((currentStep / this.totalSteps) * 100)
    };
    
    gtag('event', 'quiz_abandon', eventData);
  }
  
  trackCartAdd(cartData) {
    if (!window.SiteAnalytics) return;
    const items = cartData.items || [];
    items.forEach((item) => {
      window.SiteAnalytics.trackAddToCart({
        item_id:          item.id || item.variant_id || '',
        item_name:        item.product_title || '',
        item_variant:     item.variant_title || '',
        source_component: 'quiz_main',
        price:            (item.price || 0) / 100,
        selling_plan_id:  item.selling_plan_allocation?.selling_plan?.id || null,
        quiz_id:          this.quizId,
        // additional properties used for AB testing
        test_active:      this.testConfig.active,
        test_id:          this.testConfig.uniqueTestId,
        user_variant:     this.testConfig.userVariant
      });
    });
  }
  
  trackError(errorType, errorDetails) {
    const eventData = {
      quiz_id: this.quizId,
      quiz_session_id: this.sessionId,
      ac_session_id: window.SiteAnalytics?.sessionId || null,
      error_type: errorType,
      error_details: JSON.stringify(errorDetails),
      current_step: this.currentStep,
      time_on_quiz: Math.round((Date.now() - this.startTime) / 1000)
    };
    
    gtag('event', 'quiz_error', eventData);
  }
  }

  // Export for use in quiz element
  window.QuizAnalytics = QuizAnalytics;
} 